import { Button, Callout, FormGroup, InputGroup, Intent } from '@blueprintjs/core'
import { lang } from 'botpress/shared'
import axios from 'axios'
import cx from 'classnames'
import React, { FC, useState } from 'react'
import { Link, RouteComponentProps } from 'react-router-dom'

import LoginContainer from './LoginContainer'
import style from './style.scss'

type Props = RouteComponentProps<{ strategy?: string }>

/**
 * Step 1 of the self-service reset: ask for the email.
 *
 * The server answers the same thing whether or not the account exists, and this
 * screen shows that answer verbatim — never "no account with that email", which
 * would turn the form into an account enumeration oracle.
 */
export const ForgotPassword: FC<Props> = props => {
  const strategy = props.match.params.strategy || 'default'

  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [confirmation, setConfirmation] = useState<string>()
  const [error, setError] = useState<string>()
  const [submitting, setSubmitting] = useState(false)

  const onSubmit = async e => {
    e.preventDefault()
    setSubmitting(true)
    setError(undefined)

    try {
      const { data } = await axios.post(`${window['API_PATH']}/auth/forgot-password/${strategy}`, { email })
      setSent(true)
      setConfirmation(data?.message)
    } catch (err) {
      if (err.response?.status === 404) {
        setError(lang.tr('admin.passwordResetDisabled'))
      } else if (err.response?.status === 429) {
        setError(lang.tr('admin.passwordResetTooManyRequests'))
      } else {
        setError(lang.tr('admin.passwordResetGenericError'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (sent) {
    return (
      <LoginContainer subtitle={lang.tr('admin.checkYourEmail')}>
        <Callout intent={Intent.SUCCESS}>{confirmation || lang.tr('admin.passwordResetRequested')}</Callout>
        <p className={style.forgotPasswordHint}>{lang.tr('admin.passwordResetSpamHint')}</p>
        <Link to="/login">{lang.tr('admin.backToLogin')}</Link>
      </LoginContainer>
    )
  }

  return (
    <LoginContainer subtitle={lang.tr('admin.forgotPasswordSubtitle')} error={error}>
      <form onSubmit={onSubmit}>
        <FormGroup className={cx('label_input', style.label_input)} label={lang.tr('email')}>
          <InputGroup
            tabIndex={1}
            value={email}
            onChange={e => setEmail(e.target.value)}
            type="text"
            id="email-forgot-password"
            autoFocus={true}
          />
        </FormGroup>

        <Button
          className={cx('login_btn_tgc', style.login_btn_tgc)}
          tabIndex={2}
          type="submit"
          id="btn-send-reset-link"
          text={lang.tr('admin.sendResetLink')}
          disabled={!email || submitting}
          intent={Intent.PRIMARY}
        />
      </form>

      <p className={style.forgotPasswordHint}>
        <Link to="/login">{lang.tr('admin.backToLogin')}</Link>
      </p>
    </LoginContainer>
  )
}

export default ForgotPassword
